"use client";

import { useAuth } from "@/hooks/useAuth";
import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { FiUser, FiLogOut, FiChevronDown, FiSettings, FiStar, FiZap, FiDollarSign } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet } from "lucide-react";
import { usePoints } from "@/contexts/PointsContext";
import { useLoginModal } from "@/contexts/LoginModalContext";
import { usePrivy } from "@privy-io/react-auth";
import { useUserProfile } from "@/contexts/UserProfileContext";
import SignInButton from "./SignInButton";

export default function AuthButton() {
  const { isLoading, isReady, isLoggedIn, userId, walletAddress, user, login, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const { currentPoints, updatePoints } = usePoints();
  const { openLoginModal } = useLoginModal();
  const { profile } = useUserProfile();

  const customUsername = profile?.user?.username || null;
  const customImage = profile?.user?.image || null;
  const karma = Number(profile?.user?.karma) || 0;

  // NOTE: this component does NOT register itself as the FP animation
  // target. The desktop FP target is the sidebar FP card (registered
  // by ProfileWidget via setFPTargetRef); the mobile target is the
  // header avatar (registered by MobileUserAvatar via
  // setMobileTargetRef). When AuthButton previously registered itself
  // here, it raced with ProfileWidget and the pill ended up flying
  // toward whichever component mounted last.

  // Sync points from profile context
  useEffect(() => {
    if (profile?.user?.points != null) {
      updatePoints(Number(profile.user.points) || 0);
    }
  }, [profile?.user?.points, updatePoints]);

  // Show loading state while Privy initializes
  if (isLoading) {
    return (
      <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-gray-700 animate-pulse" />
    );
  }

  // Not isLoggedIn — show sign in button. Shared with the mobile header
  // so visual identity stays consistent across breakpoints.
  if (!isLoggedIn) {
    return <SignInButton onClick={login} />;
  }

  // Get user info from Privy
  const userEmail = user?.email?.address;
  const userWallet = user?.wallet?.address;

  // Priority: custom username > email > wallet address
  const displayName = customUsername
    ? `@${customUsername}`
    : userEmail?.split("@")[0] ||
      (userWallet ? `${userWallet.slice(0, 6)}...${userWallet.slice(-4)}` : "User");

  // Use custom image if available
  const avatarImage = customImage;

  return (
    <div className="relative flex items-center gap-2">
      {/* Points & Karma counters - Neon Style */}
      <div className="hidden sm:flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] bg-gradient-to-r from-[#CCFF00]/10 to-[#00FF9D]/10 border border-[#CCFF00]/30 hover:border-[#CCFF00] transition-all duration-300">
          <FiStar className="w-3.5 h-3.5 text-[#CCFF00]" />
          <span className="text-xs font-bold text-gray-900 dark:text-white">{currentPoints.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] bg-gradient-to-r from-yellow-400/10 to-orange-400/10 border border-yellow-400/30 hover:border-yellow-400 transition-all duration-300">
          <FiZap className="w-3.5 h-3.5 text-yellow-400" />
          <span className="text-xs font-bold text-gray-900 dark:text-white">{karma}</span>
        </div>
      </div>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0F0F12] border border-gray-200 dark:border-gray-800 hover:border-[#CCFF00]/50 dark:hover:border-[#CCFF00]/50 hover:bg-gray-200 dark:hover:bg-gray-900 transition-all duration-300"
      >
        {avatarImage ? (
          <div className="relative w-7 h-7 rounded-[5px] overflow-hidden ring-2 ring-[#CCFF00]/20">
            <Image
              src={avatarImage}
              alt={displayName}
              fill
              className="object-cover"
            />
          </div>
        ) : (
          <div className="w-7 h-7 rounded-[5px] bg-gradient-to-r from-[#CCFF00] to-[#00FF9D] flex items-center justify-center">
            <FiUser className="w-4 h-4 text-[#0A0A0A]" />
          </div>
        )}
        <span className="text-sm font-bold text-gray-900 dark:text-white hidden sm:block">
          {displayName}
        </span>
        <FiChevronDown className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute right-0 top-full mt-3 w-52 bg-white dark:bg-[#0F0F12] rounded-[5px] shadow-2xl border border-gray-200 dark:border-[#CCFF00]/20 py-2 z-50"
            >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {displayName}
              </p>
              {userEmail && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {userEmail}
                </p>
              )}
            </div>
            {customUsername && (
              <Link
                href={`/profile/@${customUsername}`}
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-[#CCFF00] dark:hover:text-[#CCFF00] flex items-center gap-2 transition-all duration-200"
              >
                <FiUser className="w-4 h-4" />
                My Profile
              </Link>
            )}
            <Link
              href="/settings"
              prefetch={false}
              onClick={() => setIsOpen(false)}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-[#CCFF00] dark:hover:text-[#CCFF00] flex items-center gap-2 transition-all duration-200"
            >
              <FiSettings className="w-4 h-4" />
              Edit Profile
            </Link>
            <Link
              href="/affiliate"
              prefetch={false}
              onClick={() => setIsOpen(false)}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-[#CCFF00] dark:hover:text-[#CCFF00] flex items-center gap-2 transition-all duration-200"
            >
              <FiDollarSign className="w-4 h-4" />
              Affiliate
            </Link>
            <Link
              href="/settings"
              prefetch={false}
              onClick={() => setIsOpen(false)}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-[#CCFF00] dark:hover:text-[#CCFF00] flex items-center gap-2 transition-all duration-200"
            >
              <Wallet className="w-4 h-4" />
              Manage Wallet
            </Link>
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-all duration-200"
            >
              <FiLogOut className="w-4 h-4" />
              Sign Out
            </button>
          </motion.div>
        </>
      )}
      </AnimatePresence>
    </div>
  );
}
